
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import IconButton from "@/components/UI/IconButton.vue";
import PanelHeader from "@/components/Reusable/Util/PanelHeader.vue";
import { MediaInputValue, MediaOptions, Media } from "@/types/media";
import WPMediaInput from "@/components/Reusable/CustomInputs/MediaInput/WPMediaInput.vue";
import { documentStore, singularityStore, uiStore } from "@/store/store";
import { findNode } from "@/lib/tree/tree";
import lodash from "lodash";
import { UnsplashImage } from "@/futurelayer/api/unsplash";
import FutureLayerImageBrowser from "@/components/Singularity/FutureLayerImageBrowser/FutureLayerImageBrowser.vue";
import { convertUnsplashImageToMediaInputValue } from "@/futurelayer/images/util";
import { getFutureLayerImageMetaFromNode } from "@/futurelayer/images/meta";
import {
  shouldDownloadImageToMediaLibrary,
  downloadImageToMediaLibrary,
  getMediaInputValueFromMediaUploadResponse
} from "@/futurelayer/images/media-library";
import { getLikelyWidthForImage, getOptimalImageUrl } from "@/futurelayer/images/dimensions";
import SingularityPanelHeader from "@/components/Singularity/PanelHeader/SingularityPanelHeader.vue";
import GalleryStrip from "./GalleryStrip.vue";
import SingularityBadge from "@/components/Singularity/components/SingularityBadge.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import SingularityOption, {
  OptionTab
} from "../components/SingularityOption.vue";
import MediaUploadDialog from "@/components/Reusable/CustomInputs/MediaInput/MediaUploadDialog.vue";
import SingularityMediaUploadDialog from "./SingularityMediaUploadDialog.vue";
import MediaPreview from "@/components/Reusable/CustomInputs/MediaInput/MediaPreview.vue";
import MediaChooserLayer from "@/components/Reusable/CustomInputs/MediaInput/MediaChooserLayer.vue";
import FutureLayerImageSuggested from "@/components/Singularity/FutureLayerImageBrowser/FutureLayerImageSuggested.vue";
import Icon from "@/components/UI/Icon.vue";
import { keyboardShortcuts } from "@/lib/keyboard-shortcuts/keyboard-shortcuts";
import { KEYBOARD_SHORTCUTS } from "@/lib/keyboard-shortcuts/global-shortcuts";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import { logEvent } from "@/util/tracking";

enum EditorView {
  Main = "main",
  Options = "options",
  UploadDialog = "uploadDialog",
  StockImages = "stockImages",
  Loading = "loading"
}

@Component({
  components: {
    Dropdown,
    IconButton,
    PanelHeader,
    WPMediaInput,
    FutureLayerImageBrowser,
    SingularityPanelHeader,
    GalleryStrip,
    SingularityBadge,
    NiceButton,
    SingularityOption,
    MediaUploadDialog,
    SingularityMediaUploadDialog,
    MediaPreview,
    MediaChooserLayer,
    FutureLayerImageSuggested,
    Icon,
    TextInput
  }
})
export default class ImageEditor extends Vue {
  originalMediaValue: MediaInputValue | null = null;
  pathToImageControlWhenInGalleryMode: string | null = null;
  pathToImageCaptionWhenInGalleryMode: string | null = null;

  editorView: EditorView = EditorView.Main;

  EditorView = EditorView;

  options: OptionTab[] = [
    {
      label: "File Upload",
      value: "file_upload",
      icon: "UploadFileIcon",
      description: "Upload from your computer.",
      action: () => {
        this.openWPMediaUploadDialog("upload");
      }
    },
    {
      label: "Browse Library",
      value: "browse_library",
      icon: "ImageIcon",
      description: "Choose from your media library.",
      action: () => {
        this.openWPMediaUploadDialog("library");
      }
    },
    {
      label: "Curated for You",
      value: "stock",
      icon: "SparklesIcon",
      description: "Search thousands of free images.",
      action: () => {
        this.setEditorView(EditorView.StockImages);
        logEvent("FutureLayer Image Editor Open Stock Images");
      }
    }
  ];

  activeWPMediaDialogTab = "library";

  mounted() {
    keyboardShortcuts.register(KEYBOARD_SHORTCUTS.SHIFT_ESC, () => {
      this.setEditorView(EditorView.Main);
    });

    if (this.initialStep === "options") {
      this.setEditorView(EditorView.Options);
    } else {
      this.setEditorView(EditorView.Main);
    }
  }

  beforeDestroy() {
    keyboardShortcuts.unbind(KEYBOARD_SHORTCUTS.SHIFT_ESC);
  }

  setEditorView(view?: EditorView) {
    this.editorView = view || EditorView.Main;
  }

  setOptionView() {
    this.setEditorView(EditorView.Options);
  }

  get isMainView() {
    return this.editorView === EditorView.Main;
  }
  get isOptionsView() {
    return this.editorView === EditorView.Options;
  }
  get isUploadDialogView() {
    return this.editorView === EditorView.UploadDialog;
  }
  get isStockImagesView() {
    return this.editorView === EditorView.StockImages;
  }
  get isLoadingView() {
    return this.editorView === EditorView.Loading;
  }

  get backState() {
    if (this.isOptionsView) {
      return this.setEditorView;
    }

    if (this.isStockImagesView) {
      return this.setOptionView;
    }

    return "page_structure";
  }

  get childTitle() {
    if (this.isOptionsView) {
      return "Update";
    }

    if (this.isStockImagesView) {
      return "Stock Images";
    }

    return null;
  }

  get imageHasUrl() {
    return (
      this.currentMediaInputValue?.url &&
      this.currentMediaInputValue.url.length > 0
    );
  }

  get currentCaptionValue() {
    if (
      !this.elementId ||
      !this.pathToImageCaptionWhenInGalleryMode ||
      !this.isGalleryMode
    )
      return "";

    const node = findNode(documentStore.document.tree, this.elementId);
    if (!node) return "";

    const result = lodash.get(
      node.data.properties,
      this.pathToImageCaptionWhenInGalleryMode
    );
    return result || "";
  }

  updateCaption(newCaption: string) {
    if (
      !this.elementId ||
      !this.pathToImageCaptionWhenInGalleryMode ||
      !this.isGalleryMode
    )
      return;

    documentStore.unthrottledPropertyChanged({
      elementId: this.elementId,
      path: this.pathToImageCaptionWhenInGalleryMode,
      value: newCaption,
      meta: {
        snapshotLabel: `Gallery Image ${
          this.selectedImageIndex + 1
        } Caption Update`
      }
    });
  }

  openWPMediaUploadDialog(showWPMediaUploadTab: "upload" | "library") {
    this.activeWPMediaDialogTab = showWPMediaUploadTab;
    this.setEditorView(EditorView.UploadDialog);
    logEvent("FutureLayer Image Editor Open WP Media Dialog", {
      tab: showWPMediaUploadTab
    });
  }

  get panelTitle() {
    return this.isGalleryMode ? "Edit Gallery" : "Edit Image";
  }

  get elementId() {
    return singularityStore.imageEditorPanelState?.elementId;
  }

  get basePathToImageControl() {
    return singularityStore.imageEditorPanelState?.controlPath;
  }

  get selectedImageIndex() {
    return singularityStore.imageEditorPanelState?.selectedImageIndex ?? 0;
  }

  get initialStep() {
    return singularityStore.imageEditorPanelState?.initialStep ?? "main";
  }

  get isGalleryMode() {
    return this.basePathToImageControl?.includes("[]") || false;
  }

  get pathToImageControl() {
    return this.isGalleryMode && this.pathToImageControlWhenInGalleryMode
      ? this.pathToImageControlWhenInGalleryMode
      : this.basePathToImageControl;
  }

  get futureLayerImageData() {
    const node = findNode(documentStore.document.tree, this.elementId);
    if (!node) return null;
    return getFutureLayerImageMetaFromNode(node);
  }

  get node() {
    return findNode(documentStore.document.tree, this.elementId);
  }

  get futureLayerImageDataForCurrentImage() {
    const futureLayerData = this.futureLayerImageData;
    if (this.isGalleryMode) {
      return futureLayerData?.[this.selectedImageIndex] || null;
    }
    return futureLayerData?.[0] || null;
  }

  get currentMediaInputValue() {
    if (!this.elementId || !this.pathToImageControl) return null;

    const node = findNode(documentStore.document.tree, this.elementId);
    if (!node) return null;

    const result = lodash.get(node.data.properties, this.pathToImageControl);
    return result || null;
  }

  updateMediaInputValue(value: MediaInputValue | null) {
    if (!this.elementId || !this.pathToImageControl) return;

    documentStore.unthrottledPropertyChanged({
      elementId: this.elementId,
      path: this.pathToImageControl,
      value: value,
      meta: {
        snapshotLabel: this.isGalleryMode
          ? `Gallery Image ${this.selectedImageIndex + 1} Update`
          : "Image Editor Media Input Update"
      }
    });
  }

  updateSelectedImageIndex(index: number) {
    if (!this.elementId || !this.basePathToImageControl) return;

    singularityStore.setImageEditorPanelState({
      elementId: this.elementId,
      controlPath: this.basePathToImageControl,
      selectedImageIndex: index,
      initialStep: this.initialStep
    });
  }

  handleImagePreviewed(optimalUrl: string) {
    if (!this.originalMediaValue) {
      this.originalMediaValue = this.currentMediaInputValue;
    }

    const previewValue: Media = {
      type: "external_image",
      url: optimalUrl,
      alt: "",
      caption: "",
      id: -1
    };

    this.updateMediaInputValue(previewValue);
  }

  handleImageRestored() {
    if (this.originalMediaValue) {
      this.updateMediaInputValue(this.originalMediaValue);
      this.originalMediaValue = null;
    }
  }

  async handleImageSelected(image: UnsplashImage) {

    if (!this.node) return;

    this.originalMediaValue = null;

    this.setEditorView(EditorView.Loading);

    if (shouldDownloadImageToMediaLibrary(image)) {
      const optimalUrl = getOptimalImageUrl(
        image,
        getLikelyWidthForImage(this.node)
      );

      const result = await downloadImageToMediaLibrary(
        optimalUrl,
        image,
        new AbortController()
      );
      if (result) {
        const mediaValue = getMediaInputValueFromMediaUploadResponse(result);
        this.updateMediaInputValue(mediaValue);
        this.setEditorView(EditorView.Main);
        return;
      }
    }

    const mediaValue = convertUnsplashImageToMediaInputValue(image);
    this.updateMediaInputValue(mediaValue);
    this.setEditorView(EditorView.Main);
  }

  addIsOpen = false;
  openAdd() {
    this.addIsOpen = !this.addIsOpen;
  }

  get mediaInputOptions(): MediaOptions {
    return {
      acceptedFileTypes: ["image"]
    };
  }

  get uiStoreActiveElementId() {
    return uiStore.activeElementId;
  }

  @Watch("uiStoreActiveElementId")
  closePanelIfElementIdChanges() {
    if (this.elementId !== this.uiStoreActiveElementId) {
      singularityStore.setImageEditorPanelState(null);
    }
  }
}
